<template>
  <modal-vue
    @close="handleToggleModal"
    :modalActive="modals[modalName]"
    max-width="!w-[80vw] !max-w-[80vw]"
    min-height="90vh"
    max-height="90vh"
    class="figma-custom-modal"
    :has-header-border="true"
  >
    <template #header>
      <p class="font-bold text-lg text-center text-textPrimary">Editar Escala</p>
    </template>

    <div class="flex flex-1 min-h-0">
      <!-- overflow-y-auto + shrink-0 nos itens: com muitas abas o menu rola em vez de
           achatar os botões e cortar o último. -->
      <nav
        class="ds-scrollbar w-[320px] shrink-0 overflow-y-auto bg-[#F0F0F4] border-r border-[#D8D8E2] flex flex-col"
      >
        <template v-for="(label, index) in tabs" :key="label">
          <span
            v-if="label === TAB_RESTRICTION_POLICY"
            class="shrink-0 px-4 pt-4 pb-2 text-left text-base font-normal leading-6 tracking-[0.08px] text-[#68688D]"
          >
            Políticas
          </span>
          <button
            :data-testid="`edit-schedule-tab-${tabTestIds[label]}-button`"
            @click="changeTab(index + 1)"
            class="flex shrink-0 items-center gap-4 px-4 py-4 text-left transition-colors duration-200"
            :class="
              tab === index + 1
                ? 'bg-[#2F7F91] text-white font-bold'
                : 'text-[#16161D] hover:bg-[#E8E8EE]'
            "
          >
            <v-icon
              size="24"
              :icon="tabIcons[label]"
              :color="tab === index + 1 ? 'white' : '#16161D'"
            />
            <span class="text-base leading-6 whitespace-nowrap">{{ label }}</span>
          </button>
        </template>
      </nav>

      <div class="flex-1 flex flex-col min-w-0">
        <div class="ds-scrollbar flex-1 overflow-y-auto p-6">
          <h2 class="font-bold text-xl leading-6 text-[#323232] mb-6 text-left">
            {{ currentTabName }}
          </h2>
          <ScheduleBasicInfo
            v-if="tabs[tab - 1] === TAB_BASIC_INFO"
            ref="basicInfoTabRef"
            :schedule-id="props.scheduleDetails.id"
            v-model:isSaving="isSaving"
            @update:modelValue="handleUpdate"
          />
          <ScheduleRequirementsTab
            v-if="tabs[tab - 1] === TAB_REQUIREMENTS"
            ref="requirementsTabRef"
            :schedule-id="props.scheduleDetails.id"
            v-model:isSaving="isSaving"
            @update:requirements="handleUpdateRequirements"
            @open-policy-tab="goToAttendancePolicyTab"
          />
          <ExchangeConfigTab
            v-if="tabs[tab - 1] === TAB_EXCHANGE_CONFIG"
            ref="exchangeConfigTabRef"
            :schedule-id="props.scheduleDetails.id"
            v-model:isSaving="isSaving"
          />
          <ScheduleApprovalFlowTab
            v-if="tabs[tab - 1] === TAB_EXCEPTIONALITY"
            ref="approvalFlowTabRef"
            :schedule-id="props.scheduleDetails.id"
            :company-id="props.scheduleDetails.companyId"
            v-model:isSaving="isSaving"
          />
          <ScheduleDsAttendancePolicyTab
            v-if="tabs[tab - 1] === TAB_ATTENDANCE_POLICY"
            ref="schaleRegistroTabRef"
            :schedule-id="props.scheduleDetails.id"
            :schedule-end-date="props.scheduleDetails.endDate"
            :is-saving="isSaving"
            v-model:hasUnsavedChanges="hasUnsavedChanges"
          />
          <PolicyLinksTab
            v-if="tabs[tab - 1] === TAB_RESTRICTION_POLICY"
            ref="policyLinksTabRef"
            :schedule-id="props.scheduleDetails.id"
            :schedule-end-date="props.scheduleDetails.endDate"
            v-model:isSaving="isSaving"
            v-model:hasUnsavedChanges="hasUnsavedChanges"
          />
          <QualificationTab
            v-if="tabs[tab - 1] === TAB_QUALIFICATION"
            :schedule-id="props.scheduleDetails.id"
          />
          <QualificationDocTab
            v-if="tabs[tab - 1] === TAB_QUALIFICATION_DOC"
            :schedule-id="props.scheduleDetails.id"
          />
          <PolicyPaymentTab
            v-if="tabs[tab - 1] === TAB_PAYMENT_POLICY"
            ref="policyPaymentTabRef"
            :schedule-id="props.scheduleDetails.id"
            @create-policy="handleCreatePolicy"
            @edit-policy="handleEditPolicy"
            @loaded-requirements="handleRequirementsLoaded"
          />
          <QualificationRequirementTab
            v-if="tabs[tab - 1] === TAB_QUALIFICATION_REQUIREMENT"
            ref="qualificationRequirementTabRef"
            :schedule-id="props.scheduleDetails.id"
            :company-id="props.scheduleDetails.companyId"
            v-model:isSaving="isSaving"
          />
        </div>

        <div class="shrink-0 px-6 pb-6">
          <div class="flex gap-6 w-full">
            <OutlinedButton class="flex-1" text="Fechar" height="48px" @click="handleToggleModal" />
            <filled-button
              v-if="hasSaveAction"
              class="flex-1"
              :disabled="saveDisabled"
              :loading="isSaving"
              color="primary30"
              :text="currentSaveLabel"
              height="48px"
              @click="handleSave"
            />
          </div>
        </div>
      </div>
    </div>
  </modal-vue>

  <div v-if="showUnsavedAlert" class="unsaved-overlay" @click.self="cancelLeave">
    <div class="unsaved-card">
      <div class="unsaved-header">
        <p class="unsaved-title">Sair da aba {{ currentTabName }}?</p>
        <button class="unsaved-close" @click="cancelLeave">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M1 1L13 13M1 13L13 1"
              stroke="#323232"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
      <div class="unsaved-content">
        <p class="unsaved-message">
          Você tem edições não salvas na aba {{ currentTabName?.toLowerCase() }}. Se sair sem
          salvar, as alterações serão perdidas
        </p>
        <div class="unsaved-actions">
          <button class="unsaved-btn-voltar" @click="cancelLeave">Voltar</button>
          <button class="unsaved-btn-sair" @click="confirmLeave">Sair</button>
        </div>
      </div>
    </div>
  </div>

  <PolicyPaymentModal
    :visible="isCreatingPolicy || isEditingPolicy"
    :edit="isEditingPolicy"
    :policy="editedPaymentPolicy"
    :scheduleId="props.scheduleDetails.id"
    :scheduleName="props.scheduleDetails.name"
    :formation-required="requiresDoctorPaymentFormationRef"
    :schedule-details="props.scheduleDetails"
    :schedule-end-date="props.scheduleDetails.endDate"
    @close="closePolicyModal"
    @created="handlePolicyChange"
    @updated="handlePolicyChange"
  />
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import {
  mdiSwapHorizontal,
  mdiLockOpenVariant,
  mdiLocationEnter,
  mdiEmailOpenMultipleOutline,
  mdiFileDocumentOutline,
  mdiFileDocumentCheckOutline,
  mdiShieldCheckOutline
} from '@mdi/js'
import {
  hospitalBuildingIcon,
  scheduleRequirementsIcon,
  paymentPolicyIcon
} from '@/assets/icons/scheduleTabIcons'
import { SCHEDULE_SECTION_LABELS } from '@/constants/schedule-sections.constants'

const TAB_BASIC_INFO = SCHEDULE_SECTION_LABELS.basicInfo
const TAB_REQUIREMENTS = SCHEDULE_SECTION_LABELS.requirements
const TAB_EXCHANGE_CONFIG = SCHEDULE_SECTION_LABELS.exchangeConfig
const TAB_EXCEPTIONALITY = SCHEDULE_SECTION_LABELS.exceptionality
const TAB_ATTENDANCE_POLICY = SCHEDULE_SECTION_LABELS.attendancePolicy
const TAB_PAYMENT_POLICY = SCHEDULE_SECTION_LABELS.paymentPolicy
const TAB_RESTRICTION_POLICY = 'Política de restrição'
const TAB_QUALIFICATION = 'Qualificação para repasse'
const TAB_QUALIFICATION_DOC = 'Qualificação documental'
const TAB_QUALIFICATION_REQUIREMENT = 'Qualificação exigida'

import { EModals, useModalStore } from '@/pinia-store/useModalStore'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'

import ScheduleBasicInfo from './tabs/ScheduleBasicInfoTab.vue'
import ScheduleRequirementsTab from './tabs/ScheduleRequirementsTab.vue'
import PolicyLinksTab from './tabs/PolicyLinksTab.vue'
import PolicyPaymentTab from './tabs/PolicyPaymentTab/index.vue'
import ScheduleDsAttendancePolicyTab from './tabs/ScheduleDsAttendancePolicyTab.vue'
import ScheduleApprovalFlowTab from './tabs/ScheduleApprovalFlowTab.vue'
import ExchangeConfigTab from './tabs/ExchangeConfigTab.vue'
import QualificationTab from './tabs/QualificationTab.vue'
import QualificationDocTab from './tabs/QualificationDocTab.vue'
import QualificationRequirementTab from './tabs/QualificationRequirementTab.vue'

import { ScheduleResponse } from '@/Network/Types/Responses/ScheduleUnitResponses'
import PaymentPolicyService from '@/Network/Services/DsPaymentPolicyService'
import {
  PaymentPolicy,
  PaymentPolicyByIdResponse
} from '@/Network/Types/Responses/DsPaymentPolicyResponses'
import PolicyPaymentModal from './tabs/PolicyPaymentTab/PolicyPaymentModal.vue'
import { useCreateScheduleStore } from '@/pinia-store/useCreateScheduleStore'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useErrorToast } from '@/composables/useErrorToast'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'

const { flags } = useFeatureFlags()
const { notifyError } = useErrorToast()
const permissionsStore = usePermissionsStore()

const canViewExceptionality = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationExceptionalitiesExceptionViewing,
    resourceType: AuthorizationResourcesAndSubjects.StationExceptionalitiesSchedule
  })
})
const TAB_SLUGS: Record<string, string> = {
  // "Abrir configuração da escala" do segmento "Ocupado sem identificação" desativado.
  'informacoes-basicas': TAB_BASIC_INFO,
  'qualificacao-repasse': TAB_QUALIFICATION,
  'qualificacao-documental': TAB_QUALIFICATION_DOC,
  'qualificacao-exigida': TAB_QUALIFICATION_REQUIREMENT,
  // M7 (balão ⑯) — "ver política" do Detalhes do Plantão e da aba Requisitos cai
  // direto na aba da política, que é onde a exigência de check mora agora.
  'registro-de-plantao': TAB_ATTENDANCE_POLICY
}

const props = defineProps<{
  scheduleDetails: {
    id: number
    name: string
    healthUnitName: string
    companyName: string
    companyId: number
    endDate: string | null
  }
  initialTabSlug?: string | null
}>()

const modalName = EModals.EDIT_SCHEDULE
const { modals } = storeToRefs(useModalStore())
const modalStore = useModalStore()
const forceCloseModal = () => {
  showUnsavedAlert.value = false
  pendingAction.value = null
  modalStore.toggleModal(modalName)
  tab.value = 1
}

function getCurrentTabDirty(): boolean {
  const tabName = currentTabName.value
  const dirtyByRef: Record<string, { value?: { hasUnsavedChanges?: boolean } }> = {
    [TAB_BASIC_INFO]: basicInfoTabRef,
    [TAB_REQUIREMENTS]: requirementsTabRef,
    [TAB_EXCHANGE_CONFIG]: exchangeConfigTabRef,
    [TAB_EXCEPTIONALITY]: approvalFlowTabRef,
    [TAB_QUALIFICATION_REQUIREMENT]: qualificationRequirementTabRef
  }
  if (tabName in dirtyByRef) return dirtyByRef[tabName].value?.hasUnsavedChanges ?? false
  if (tabName === TAB_ATTENDANCE_POLICY || tabName === TAB_RESTRICTION_POLICY)
    return hasUnsavedChanges.value
  return false
}

function guardedAction(action: () => void) {
  if (getCurrentTabDirty()) {
    pendingAction.value = action
    showUnsavedAlert.value = true
  } else {
    action()
  }
}

const handleToggleModal = () => {
  guardedAction(forceCloseModal)
}

/**
 * M7 (Tela 4) — o bloco somente-leitura da aba Requisitos não reintroduz os
 * interruptores: ele manda para a aba onde a política os edita. Passa pelo mesmo
 * guardião de alterações não salvas que a troca de aba pelo menu.
 */
function goToAttendancePolicyTab() {
  const index = tabs.value.indexOf(TAB_ATTENDANCE_POLICY)
  if (index === -1) return
  guardedAction(() => {
    tab.value = index + 1
  })
}

const isSaving = ref(false)
const hasUnsavedChanges = ref(false)
const showUnsavedAlert = ref(false)
const pendingAction = ref<(() => void) | null>(null)

const requirementsTabRef = ref()
const basicInfoTabRef = ref()
const policyLinksTabRef = ref()
const policyPaymentTabRef = ref()
const approvalFlowTabRef = ref()
const exchangeConfigTabRef = ref()
const schaleRegistroTabRef = ref()
const qualificationRequirementTabRef = ref()
const editedRequirements = ref(null)

const tab = ref(1)

const tabs = computed(() => {
  return [
    TAB_BASIC_INFO,
    TAB_REQUIREMENTS,
    ...(flags.enabledProfessionalDocuments ? [TAB_QUALIFICATION_REQUIREMENT] : []),
    TAB_EXCHANGE_CONFIG,
    TAB_RESTRICTION_POLICY,
    // Mesma ordem relativa do wizard Criar escala: Excepcionalidade vem logo
    // depois da Política de registro de plantão e antes da de pagamento.
    TAB_ATTENDANCE_POLICY,
    ...(flags.enabledApprovalFlow && canViewExceptionality.value ? [TAB_EXCEPTIONALITY] : []),
    TAB_PAYMENT_POLICY,
    ...(flags.enabledPolicyModules ? [TAB_QUALIFICATION, TAB_QUALIFICATION_DOC] : [])
  ]
})

const tabIcons: Record<string, string> = {
  [TAB_BASIC_INFO]: hospitalBuildingIcon,
  [TAB_REQUIREMENTS]: scheduleRequirementsIcon,
  [TAB_EXCHANGE_CONFIG]: mdiSwapHorizontal,
  [TAB_EXCEPTIONALITY]: mdiLockOpenVariant,
  [TAB_ATTENDANCE_POLICY]: mdiLocationEnter,
  [TAB_PAYMENT_POLICY]: paymentPolicyIcon,
  [TAB_RESTRICTION_POLICY]: mdiFileDocumentOutline,
  [TAB_QUALIFICATION]: mdiEmailOpenMultipleOutline,
  [TAB_QUALIFICATION_DOC]: mdiFileDocumentCheckOutline,
  [TAB_QUALIFICATION_REQUIREMENT]: mdiShieldCheckOutline
}

// Slug estável por aba: os labels podem mudar de texto sem quebrar os testes E2E.
const tabTestIds: Record<string, string> = {
  [TAB_BASIC_INFO]: 'basic-info',
  [TAB_REQUIREMENTS]: 'requirements',
  [TAB_EXCHANGE_CONFIG]: 'exchange-config',
  [TAB_EXCEPTIONALITY]: 'exceptionality',
  [TAB_ATTENDANCE_POLICY]: 'attendance-policy',
  [TAB_PAYMENT_POLICY]: 'payment-policy',
  [TAB_RESTRICTION_POLICY]: 'restriction-policy',
  [TAB_QUALIFICATION]: 'qualification',
  [TAB_QUALIFICATION_DOC]: 'qualification-doc',
  [TAB_QUALIFICATION_REQUIREMENT]: 'qualification-requirement'
}

const currentTabName = computed(() => tabs.value[tab.value - 1])

const saveLabels: Record<string, string> = {
  [TAB_BASIC_INFO]: 'Salvar informações básicas',
  [TAB_REQUIREMENTS]: 'Salvar requisitos da escala',
  [TAB_EXCHANGE_CONFIG]: 'Salvar trocas e jornada',
  [TAB_EXCEPTIONALITY]: 'Salvar excepcionalidade',
  [TAB_RESTRICTION_POLICY]: 'Salvar política de restrição',
  [TAB_QUALIFICATION_REQUIREMENT]: 'Salvar qualificação exigida'
}

const currentSaveLabel = computed(() => saveLabels[currentTabName.value] || '')
const hasSaveAction = computed(() => !!currentSaveLabel.value)

const saveDisabled = computed(() => {
  if (currentTabName.value === TAB_BASIC_INFO) return isSaving.value || !editedSchedule.value
  if (currentTabName.value === TAB_EXCHANGE_CONFIG)
    return isSaving.value || exchangeConfigTabRef.value?.isFormValid === false
  if (currentTabName.value === TAB_RESTRICTION_POLICY)
    return !hasUnsavedChanges.value || isSaving.value
  if (currentTabName.value === TAB_QUALIFICATION_REQUIREMENT)
    return isSaving.value || qualificationRequirementTabRef.value?.isFormValid === false
  return isSaving.value
})

const editedSchedule = ref<ScheduleResponse | null>(null)

const service = new PaymentPolicyService()

const isCreatingPolicy = ref(false)
const isEditingPolicy = ref(false)
const editedPaymentPolicy = ref<PaymentPolicyByIdResponse | null>(null)
const requiresDoctorPaymentFormationRef = ref(false)
const scheduleStore = useCreateScheduleStore()

const closePolicyModal = () => {
  isCreatingPolicy.value = false
  isEditingPolicy.value = false
  editedPaymentPolicy.value = null

  modalStore.toggleModal(EModals.EDIT_SCHEDULE)
}

const handleCreatePolicy = () => {
  modalStore.toggleModal(EModals.EDIT_SCHEDULE)
  scheduleStore.resetStep4()
  isCreatingPolicy.value = true
}

const handlePolicyChange = () => {
  closePolicyModal()
  policyPaymentTabRef.value?.fetchPolicies()
}

const handleEditPolicy = async (policy: PaymentPolicy) => {
  try {
    const response = await service.getById(policy.id)
    editedPaymentPolicy.value = response

    modalStore.toggleModal(EModals.EDIT_SCHEDULE)

    isEditingPolicy.value = true
  } catch (error) {
    console.error('Erro ao carregar política para edição:', error)
    notifyError(error, 'Não foi possível abrir a política de pagamento para edição.')
  }
}

const handleRequirementsLoaded = (requiresFormation: boolean) => {
  requiresDoctorPaymentFormationRef.value = requiresFormation
}

const handleUpdate = async (newSchedule: ScheduleResponse) => {
  editedSchedule.value = newSchedule
}

function handleUpdateRequirements(data: any) {
  editedRequirements.value = data
}

const changeTab = (index: number) => {
  if (index === tab.value) return
  guardedAction(() => {
    tab.value = index
  })
}

const confirmLeave = () => {
  showUnsavedAlert.value = false
  pendingAction.value?.()
  pendingAction.value = null
}

const cancelLeave = () => {
  showUnsavedAlert.value = false
  pendingAction.value = null
}

watch(
  () => modals.value[modalName],
  (isOpen) => {
    if (!isOpen || !props.initialTabSlug) return
    const label = TAB_SLUGS[props.initialTabSlug]
    if (!label) return
    const index = tabs.value.indexOf(label)
    if (index !== -1) {
      tab.value = index + 1
    }
  }
)

const handleSave = async () => {
  const currentTab = currentTabName.value

  if (currentTab === TAB_BASIC_INFO) {
    await basicInfoTabRef.value?.handleSave()
  } else if (currentTab === TAB_REQUIREMENTS) {
    await requirementsTabRef.value?.handleSave()
  } else if (currentTab === TAB_EXCHANGE_CONFIG) {
    await exchangeConfigTabRef.value?.handleSave()
    forceCloseModal()
  } else if (currentTab === TAB_ATTENDANCE_POLICY) {
    await ScheduleDsAttendancePolicyTab.value?.handleSave()
  } else if (currentTab === TAB_EXCEPTIONALITY) {
    await approvalFlowTabRef.value?.handleSave()
  } else if (currentTab === TAB_RESTRICTION_POLICY) {
    await policyLinksTabRef.value?.handleSave()
  } else if (currentTab === TAB_QUALIFICATION_REQUIREMENT) {
    await qualificationRequirementTabRef.value?.handleSave()
  }
}
</script>

<style scoped>
.figma-custom-modal :deep(.modal-content) {
  border: 1px solid #d8d8e2 !important;
  border-radius: 8px !important;
  background-color: #ffffff !important;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08) !important;
  align-items: flex-start !important;
}
.figma-custom-modal :deep(main) {
  width: 100%;
  padding: 0 !important;
  overflow: hidden !important;
}

.unsaved-overlay {
  position: fixed;
  inset: 0;
  z-index: 210;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.48);
  backdrop-filter: blur(12px);
}

.unsaved-card {
  width: 525px;
  max-width: calc(100% - 32px);
  background: #ffffff;
  border: 1px solid #d8d8e2;
  border-radius: 8px;
  overflow: hidden;
  box-shadow:
    0px 4px 4px -1px rgba(12, 12, 13, 0.1),
    0px 4px 4px -1px rgba(12, 12, 13, 0.05);
}

.unsaved-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 0 16px;
}

.unsaved-title {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  color: #323232;
  text-align: center;
  margin: 0;
}

.unsaved-close {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.unsaved-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  padding: 16px;
}

.unsaved-message {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.08px;
  color: #16161d;
  text-align: center;
  padding: 8px 0;
  margin: 0;
}

.unsaved-actions {
  display: flex;
  gap: 24px;
  align-items: center;
  width: 100%;
}

.unsaved-btn-voltar,
.unsaved-btn-sair {
  flex: 1;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.07px;
  cursor: pointer;
  white-space: nowrap;
}

.unsaved-btn-voltar {
  background: #2f7f91;
  color: #ffffff;
  border: none;
}

.unsaved-btn-sair {
  background: #ffffff;
  color: #da1e28;
  border: 2px solid #da1e28;
}
</style>
